import React, { useEffect, useMemo, useState } from "react";


const REF_ENDPOINTS = {
  subjects: "/publisher/ref/subjects",
  categories: "/publisher/ref/categories",
  criteriaForms: "/publisher/ref/criteria-forms",
  directions: "/publisher/ref/directions",
  educationLevels: "/publisher/ref/education-levels",
  grades: "/publisher/ref/grades",
  languages: "/publisher/ref/languages",
  publicationTypes: "/publisher/ref/publication-types",
  serviceTypes: "/publisher/ref/service-types",
  years: "/publisher/ref/years",
};

const emptyForm = {
  title: "",
  subject_id: "",
  direction_id: "",
  education_level_id: "",
  grade_id: "",
  publication_type_id: "",
  service_type_id: "",
  development_language_id: "",
  teaching_language_id: "",
  submission_year_id: "",
};

const pickGroup = (payload) =>
  payload?.group || payload?.data?.group || payload?.data || payload || {};

const toId = (v) => (v == null ? "" : String(typeof v === "object" ? v.id : v));

const pickArr = (resp) => {
  const d = resp?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.items)) return d.items;
  return [];
};

export default function EditGroupModal({
  open,
  locale,
  t,
  groupId,
  axios,
  refs,
  refsLoading,
  loadAllRefs, // оставляем как есть
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const optLabel = useMemo(
    () => (o) => o?.[`name_${locale}`] || o?.name || o?.title || String(o?.id ?? ""),
    [locale]
  );

  const FieldError = ({ name }) => {
    const msg = errors?.[name]?.[0];
    if (!msg) return null;
    return (
      <div className="gl-error" style={{ marginTop: 6 }}>
        {msg}
      </div>
    );
  };

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ✅ Подстраховка: догрузить ключевые refs (БЕЗ criteria/categories)
  const ensureRefs = async () => {
    const need = {
      subjects: refs?.subjects,
      directions: refs?.directions,
      educationLevels: refs?.educationLevels,
      grades: refs?.grades,
      publicationTypes: refs?.publicationTypes,
      serviceTypes: refs?.serviceTypes,
      developmentLanguages: refs?.developmentLanguages,
      teachingLanguages: refs?.teachingLanguages,
      submissionYears: refs?.submissionYears,
    };

    const ok = (arr) => Array.isArray(arr) && arr.length > 0;
    const missing =
      !ok(need.subjects) ||
      !ok(need.directions) ||
      !ok(need.educationLevels) ||
      !ok(need.grades) ||
      !ok(need.publicationTypes) ||
      !ok(need.serviceTypes) ||
      !ok(need.developmentLanguages) ||
      !ok(need.teachingLanguages) ||
      !ok(need.submissionYears);

    if (!missing) return null;

    // 1) если есть loadAllRefs — сначала пробуем её
    if (loadAllRefs) {
      await loadAllRefs();
    }

    // 2) если всё равно пусто — догружаем точечно langs/years
    const stillMissing =
      !ok(refs?.developmentLanguages) ||
      !ok(refs?.teachingLanguages) ||
      !ok(refs?.submissionYears);

    if (stillMissing) {

      const [rLang, rYears] = await Promise.all([
        axios.get(REF_ENDPOINTS.languages, { headers: { "Accept-Language": locale } }),
        axios.get(REF_ENDPOINTS.years, { headers: { "Accept-Language": locale } }),
      ]);

      const languages = pickArr(rLang);
      const years = pickArr(rYears);

      // refs пропсом — поэтому используем fallback
      return { languages, years };
    }

    return null;
  };

  const [fallbackLangs, setFallbackLangs] = useState([]);
  const [fallbackYears, setFallbackYears] = useState([]);

  // load group data on open/groupId
  useEffect(() => {
    if (!open || !groupId) return;

    let alive = true;

    (async () => {
      setLoading(true);
      setSaving(false);
      setErrors({});
      setForm(emptyForm);

      try {
        const fb = await ensureRefs();
        if (fb?.languages && alive) setFallbackLangs(fb.languages);
        if (fb?.years && alive) setFallbackYears(fb.years);

        const r = await axios.get(`/publisher/groups/${groupId}`, {
          headers: { "Accept-Language": locale },
        });

        const data = pickGroup(r.data);

        const next = {
          title: data.title ?? "",
          subject_id: toId(data.subject_id ?? data.subject),
          direction_id: toId(data.direction_id ?? data.direction),
          education_level_id: toId(data.education_level_id ?? data.education_level),
          grade_id: toId(data.grade_id ?? data.grade),
          publication_type_id: toId(data.publication_type_id ?? data.publication_type),
          service_type_id: toId(data.service_type_id ?? data.service_type),
          development_language_id: toId(data.development_language_id ?? data.development_language),
          teaching_language_id: toId(data.teaching_language_id ?? data.teaching_language),
          submission_year_id: toId(data.submission_year_id ?? data.submission_year),
        };

        if (!alive) return;
        setForm(next);
      } catch (e) {
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupId, axios, locale]);

  const save = async () => {
    if (!groupId) return;

    const payload = {
      title: String(form.title || "").trim(),
      subject_id: Number(form.subject_id),
      direction_id: Number(form.direction_id),
      education_level_id: Number(form.education_level_id),
      grade_id: Number(form.grade_id),
      publication_type_id: Number(form.publication_type_id),
      service_type_id: Number(form.service_type_id),
      development_language_id: Number(form.development_language_id) || null,
      teaching_language_id: Number(form.teaching_language_id) || null,
      submission_year_id: Number(form.submission_year_id) || null,
    };

    try {
      setSaving(true);
      setErrors({});

      await axios.put(`/publisher/groups/${groupId}`, payload, {
        headers: { "Accept-Language": locale, "Content-Type": "application/json" },
      });

      await onSaved?.();
      onClose?.();
    } catch (e) {
      const api = e?.response?.data;
      setErrors(api?.errors || {});
      alert(t.saveErr);
      setSaving(false);
    }
  };

  if (!open) return null;

  const devLangs = (refs?.developmentLanguages?.length ? refs.developmentLanguages : fallbackLangs) || [];
  const teachLangs = (refs?.teachingLanguages?.length ? refs.teachingLanguages : fallbackLangs) || [];
  const years = (refs?.submissionYears?.length ? refs.submissionYears : fallbackYears) || [];

  return (
    <div className="ga-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ga-modal-card ga-modal-card--xl" onClick={(e) => e.stopPropagation()}>
        <div className="ga-modal-head">
          <h3 className="ga-modal-title">{t.editGroupTitle}</h3>
          <button className="ga-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="ga-modal-body">
          {loading && <div className="ngf-empty">...</div>}

          {!loading && (
            <div className="new-application-form" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className="form-group" style={{ gridColumn: "span 2" }}>
                <label>{t.groupName}</label>
                <input
                  className="gl-input"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                />
                <FieldError name="title" />
              </div>

              <div className="form-group">
                <label>{t.subject}</label>
                <select
                  className="gl-input"
                  value={form.subject_id}
                  onChange={(e) => setForm((p) => ({ ...p, subject_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.subjects || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="subject_id" />
              </div>

              <div className="form-group">
                <label>{t.direction}</label>
                <select
                  className="gl-input"
                  value={form.direction_id}
                  onChange={(e) => setForm((p) => ({ ...p, direction_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.directions || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="direction_id" />
              </div>

              <div className="form-group">
                <label>{t.educationLevel}</label>
                <select
                  className="gl-input"
                  value={form.education_level_id}
                  onChange={(e) => setForm((p) => ({ ...p, education_level_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.educationLevels || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="education_level_id" />
              </div>

              <div className="form-group">
                <label>{t.gradeLabel}</label>
                <select
                  className="gl-input"
                  value={form.grade_id}
                  onChange={(e) => setForm((p) => ({ ...p, grade_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.grades || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="grade_id" />
              </div>

              <div className="form-group">
                <label>{t.publicationType}</label>
                <select
                  className="gl-input"
                  value={form.publication_type_id}
                  onChange={(e) => setForm((p) => ({ ...p, publication_type_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.publicationTypes || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="publication_type_id" />
              </div>

              <div className="form-group">
                <label>{t.serviceType}</label>
                <select
                  className="gl-input"
                  value={form.service_type_id}
                  onChange={(e) => setForm((p) => ({ ...p, service_type_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {(refs.serviceTypes || []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="service_type_id" />
              </div>

              <div className="form-group">
                <label>{t.devLang}</label>
                <select
                  className="gl-input"
                  value={form.development_language_id}
                  onChange={(e) => setForm((p) => ({ ...p, development_language_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {devLangs.map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="development_language_id" />
              </div>

              <div className="form-group">
                <label>{t.teachLang}</label>
                <select
                  className="gl-input"
                  value={form.teaching_language_id}
                  onChange={(e) => setForm((p) => ({ ...p, teaching_language_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {teachLangs.map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="teaching_language_id" />
              </div>

              <div className="form-group">
                <label>{t.submissionYear}</label>
                <select
                  className="gl-input"
                  value={form.submission_year_id}
                  onChange={(e) => setForm((p) => ({ ...p, submission_year_id: e.target.value }))}
                >
                  <option value="" disabled>—</option>
                  {years.map((o) => (
                    <option key={o.id} value={String(o.id)}>{optLabel(o)}</option>
                  ))}
                </select>
                <FieldError name="submission_year_id" />
              </div>
            </div>
          )}

          <div className="gl-actions" style={{ marginTop: 12 }}>
            <button className="gl-btn" onClick={save} disabled={saving || loading || refsLoading}>
              {saving ? "..." : t.save}
            </button>
            <button className="gl-btn gl-btn--ghost" onClick={onClose} disabled={saving}>
              {t.cancel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}